import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, mergeProps as _mergeProps, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-0.5" };
const _hoisted_2 = ["id"];
const _hoisted_3 = ["id"];
const _hoisted_4 = { class: "flex flex-col gap-4 min-h-[60dvh]" };
const _hoisted_5 = { class: "w-full md:w-102" };
import { computed, ref, watch, useId } from "vue";
import { useI18n } from "vue-i18n";
import { AutoComplete, Button, Modal } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { usePermissions } from "@/shared/composables/usePermissions";
import { useProfileStore } from "@/modules/settings/stores/useProfileStore";
import { useAppToast } from "@/shared/composables/useAppToast";
import { getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import { isoToDisplayDate, displayDateToIso } from "@/shared/utils/date-formatting";
import { setRelationFunctions } from "../../infrastructure/api/contactRelationsApi";
import { logger } from "@/infrastructure/services/LoggerService";
import RelationFunctionRows from "./RelationFunctionRows.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'EditRelationModal',
    props: /*@__PURE__*/ _mergeModels({
        contactId: {},
        organisation: {}
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["updated"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const open = _useModel(__props, "modelValue");
        const props = __props;
        const emit = __emit;
        // ─── Dependencies ──────────────────────────────────────────────────────────────
        const { locale } = useI18n();
        const { canEditOphub: canEdit } = usePermissions();
        const profileStore = useProfileStore();
        const { toastSuccess, toastError } = useAppToast();
        const titleId = useId();
        const descriptionId = useId();
        // ─── Function rows ─────────────────────────────────────────────────────────────
        // EditFunctionRow extends RelationFunctionRow with the original function id for
        // the full-set reconciliation payload (id present = edit, absent = new row)
        function buildRows() {
            return props.organisation.functions.map((fn) => ({
                _id: crypto.randomUUID(),
                existingId: fn.id,
                title: fn.editTitle,
                function: fn.categoryKey ?? "",
                start: fn.startDate
                    ? isoToDisplayDate(fn.startDate, profileStore.profile?.dateFormat, locale.value)
                    : "",
                end: fn.endDate
                    ? isoToDisplayDate(fn.endDate, profileStore.profile?.dateFormat, locale.value)
                    : "",
            }));
        }
        const functionRows = ref(buildRows());
        const functionRowsRef = ref();
        // Re-initialise rows every time the modal opens (org data may have refreshed)
        watch(open, (v) => {
            if (v)
                functionRows.value = buildRows();
        });
        // ─── Save gating ───────────────────────────────────────────────────────────────
        const EDIT_RELATION_ERROR_KEYS = {
            "Relation.Title.Required": "contact.detail.relations.edit.errors.titleRequired",
            "Relation.Title.TooLong": "contact.detail.relations.edit.errors.titleTooLong",
            "Relation.EndDate.BeforeStartDate": "contact.detail.relations.errors.invalidDateRange",
        };
        const canSave = computed(() => canEdit.value &&
            functionRows.value.length > 0 &&
            functionRows.value.every((r) => !!r.function) &&
            !(functionRowsRef.value?.hasErrors ?? false));
        // ─── Submit ────────────────────────────────────────────────────────────────────
        const saving = ref(false);
        async function handleSubmit() {
            if (!canSave.value)
                return;
            saving.value = true;
            try {
                const existingIds = new Set(functionRows.value.map((r) => r.existingId).filter(Boolean));
                const deletedIds = props.organisation.functions
                    .filter((fn) => !existingIds.has(fn.id))
                    .map((fn) => fn.id);
                const upsert = functionRows.value.map((row) => ({
                    id: row.existingId,
                    title: row.title.trim(),
                    function: row.function,
                    startDate: row.start
                        ? (displayDateToIso(row.start, profileStore.profile?.dateFormat, locale.value) ?? null)
                        : null,
                    endDate: row.end
                        ? (displayDateToIso(row.end, profileStore.profile?.dateFormat, locale.value) ?? null)
                        : null,
                }));
                await setRelationFunctions(props.contactId, props.organisation.id, { upsert, deletedIds });
                toastSuccess("contact.detail.relations.edit.success");
                emit("updated");
                open.value = false;
            }
            catch (err) {
                logger.error("EditRelationModal: save failed", err);
                const code = getApiErrorCode(err);
                toastError((code && EDIT_RELATION_ERROR_KEYS[code]) ?? "contact.detail.relations.edit.error");
            }
            finally {
                saving.value = false;
            }
        }
        // ─── Cancel ────────────────────────────────────────────────────────────────────
        function handleCancel() {
            open.value = false;
            // rows are re-initialised on next open via the watch
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": [
                    _cache[1] || (_cache[1] = ($event) => ((open).value = $event)),
                    _cache[2] || (_cache[2] = (v) => !v && handleCancel())
                ],
                size: "xl",
                "header-position": "center",
                "footer-position": "split",
                "show-close-button": true,
                "show-header-border": false,
                "show-footer-border": false,
                "aria-labelledby": _unref(titleId),
                "aria-describedby": _unref(descriptionId)
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _createElementVNode("h2", {
                            id: _unref(titleId),
                            class: "text-(length:--size-xxl) font-(--weight-bold)"
                        }, _toDisplayString(_ctx.$t("contact.detail.relations.edit.title")), 9 /* TEXT, PROPS */, _hoisted_2),
                        _createElementVNode("p", {
                            id: _unref(descriptionId),
                            class: "text-(length:--size-l) text-(--text-text-body-subtle)"
                        }, _toDisplayString(_ctx.$t("contact.detail.relations.create.subtitle")), 9 /* TEXT, PROPS */, _hoisted_3)
                    ])
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _ctx.$t('common.cancel'),
                        onClick: handleCancel
                    }, null, 8 /* PROPS */, ["label"]),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        label: _ctx.$t('common.save'),
                        loading: saving.value,
                        disabled: !canSave.value,
                        onClick: handleSubmit
                    }, null, 8 /* PROPS */, ["label", "loading", "disabled"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_4, [
                        _createElementVNode("div", _hoisted_5, [
                            _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_ctx.$t), {
                                "model-value": __props.organisation.id,
                                label: _ctx.$t('contact.detail.relations.create.fields.organisation.label'),
                                placeholder: _ctx.$t('contact.detail.relations.create.fields.organisation.placeholder'),
                                "empty-label": _ctx.$t('contact.detail.relations.create.fields.organisation.empty'),
                                options: [{ value: __props.organisation.id, label: __props.organisation.name }],
                                required: true,
                                disabled: true,
                                "icon-left": "ri-building-line",
                                "icon-right": "ri-search-line",
                                "full-width": ""
                            }), null, 16 /* FULL_PROPS */, ["model-value", "label", "placeholder", "empty-label", "options"])
                        ]),
                        _createVNode(RelationFunctionRows, {
                            ref_key: "functionRowsRef",
                            ref: functionRowsRef,
                            modelValue: functionRows.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((functionRows).value = $event)),
                            "require-title": true
                        }, null, 8 /* PROPS */, ["modelValue"])
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "aria-labelledby", "aria-describedby"]));
        };
    }
});
